import {
  APP_LANGUAGE_STORAGE_KEY,
  DEFAULT_LANGUAGE,
  normalizeLanguage,
  type AppLanguage,
} from '@/lib/i18n/constants';

/** True when the user (or login flow) has explicitly stored a language preference. */
export function hasCachedAppLanguage(): boolean {
  if (typeof window === 'undefined') return false;
  const raw = localStorage.getItem(APP_LANGUAGE_STORAGE_KEY);
  return raw != null && raw.trim() !== '';
}

/** Cached UI language (guest pages + mirror of last known user preference). */
export function getCachedAppLanguage(): AppLanguage {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
  return normalizeLanguage(localStorage.getItem(APP_LANGUAGE_STORAGE_KEY));
}

/** Update cache only (no i18n switch) — e.g. after login before redirect to dashboard. */
export function primeAppLanguageCache(language: string | null | undefined): void {
  if (!language?.trim() || typeof window === 'undefined') return;
  setCachedAppLanguage(normalizeLanguage(language));
}

export function setCachedAppLanguage(lang: AppLanguage): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(APP_LANGUAGE_STORAGE_KEY, lang);
}

/** Call on logout so the next session/user does not inherit language. */
export function clearAppLanguageStorage(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(APP_LANGUAGE_STORAGE_KEY);
}

/** After login or settings fetch — API is source of truth when set; keep cache when API omits language. */
export function cacheLanguageFromUserSettings(language: string | null | undefined): AppLanguage {
  const lang = language?.trim()
    ? normalizeLanguage(language)
    : getCachedAppLanguage();
  setCachedAppLanguage(lang);
  return lang;
}
